/**
 * Ordering shared by the flag tables — the library page and a client's flag archive. Each table
 * names how its own columns compare; the `SortableTh` direction, the name tiebreak and where an
 * unset value goes live here.
 */

import { FLAG_CATEGORY_CODINGS, FlagCategory } from "../constants/flags";

/**
 * Compares two strings, ordering an empty one (an unset field) after any non-empty one.
 *
 * @param left - The first value, `""` when unset
 * @param right - The second value, `""` when unset
 * @param direction - `-1` reverses the order of two set values; an unset value still sorts last
 * @returns A negative number when `left` sorts first, positive when `right` does, `0` on a tie
 */
export function compareUnsetLast(left: string, right: string, direction: 1 | -1 = 1): number {
  if (!left || !right) return Number(!left) - Number(!right);

  return left.localeCompare(right) * direction;
}

/**
 * Orders by the category display the column shows, not by the underlying code, with an unset
 * category after "Administrative" rather than ahead of it.
 */
export function compareFlagCategories(
  a: { category: FlagCategory | null },
  b: { category: FlagCategory | null },
): number {
  return compareUnsetLast(getCategoryDisplay(a.category), getCategoryDisplay(b.category));
}

/**
 * Reorders flag rows for a sorted column.
 *
 * `sort` follows the `SortableTh` convention: a bare key ascends, a `-` prefix descends. The
 * direction reverses the whole column comparison, so an unset value that sorts last ascending sorts
 * first descending. Ties, and keys `compare` does not know, order by name.
 *
 * @param flags - The rows to order; left unmodified
 * @param sort - The `sort` search param, or null for no sorted column
 * @param compare - Orders two rows ascending by the named column
 * @returns A new sorted array, or `flags` itself when `sort` is null
 */
export function sortFlagRows<TFlag extends { name: string }, TSortKey extends string>(
  flags: TFlag[],
  sort: string | null,
  compare: (a: TFlag, b: TFlag, sortKey: TSortKey) => number,
): TFlag[] {
  if (!sort) return flags;

  const isDescending = sort.startsWith("-");
  const sortKey = (isDescending ? sort.slice(1) : sort) as TSortKey;
  const direction = isDescending ? -1 : 1;

  return [...flags].sort(
    (a, b) => compare(a, b, sortKey) * direction || a.name.localeCompare(b.name),
  );
}

/** Empty for an unset category, which `compareUnsetLast` then orders last. */
function getCategoryDisplay(category: FlagCategory | null): string {
  return category === null ? "" : FLAG_CATEGORY_CODINGS[category].display;
}
